import firebase from 'firebase/compat/app';
import { User, UserData } from 'hydra-data-types';
import { stripUndefined } from 'ts-utils';
import { ApiClient } from '../api/ApiClient';
import { fetchUserData, getUserDocRef } from '../helpers/CommonDatabaseOperations';
import { RegistrationNewUserDetails } from '../store/user/effects/RegisterUser';
import { Analytics } from './Analytics';
import {
  emailSignIn,
  emailSignInAndAccountValidation,
  fetchUser,
  identifyAndTrack,
  tokenSignIn,
} from './AuthUtils';
import { stripMedicareAccessFromUserData } from './MedicareAccessSymptomUtils';

type AuthErrorCode =
  | 'auth/no-fill-error'
  | 'auth/email-already-in-use'
  | 'auth/wrong-password'
  | 'auth/user-not-found'
  | 'auth/too-many-requests'
  | 'auth/network-request-failed'
  | 'auth/email-already-exists'
  | 'auth/invalid-email'
  | 'auth/invalid-password'
  | 'auth/invalid-credential'
  | 'auth/requires-recent-login'
  | 'auth/invalid-user-token'
  | 'auth/non-matching-email'
  | 'auth/internal-error'
  | 'auth/invalid-email-reset';

export type AuthError = {
  code: AuthErrorCode;
  message: string;
};

export type BasicLoginDetails = {
  email: string;
  password: string;
};

export type TokenLoginDetails = {
  token: string;
};

export type LoginDetails = BasicLoginDetails | TokenLoginDetails;

export type RegistrationDetails = BasicLoginDetails & {
  name: string;
  lastName?: string;
  userData?: UserData;
  isTemporaryRegister?: boolean;
};

export type AuthResponse = {
  user: User;
  userData: UserData;
};

export type SignupAndRegisterForPaymentResponse = {
  user: User;
  paid: boolean;
  /** Requires the user to set a password, which replaces the tempPassword */
  setUserPassword: boolean;
};

function isAuthError(error: unknown): error is AuthError {
  return (
    typeof (error as AuthError)?.code === 'string' &&
    typeof (error as AuthError)?.message === 'string'
  );
}

function isUserSignedIn(): boolean {
  const firebaseAuth = firebase.auth();
  return firebaseAuth.currentUser !== null;
}

async function signInUser(details: LoginDetails): Promise<AuthResponse> {
  try {
    const userId = await ('token' in details
      ? tokenSignIn(details.token)
      : emailSignIn(details.email, details.password));
    if (userId === undefined) {
      throw new Error('userId not available');
    }
    const [user, userData] = await Promise.all([fetchUser(userId), fetchUserData(userId)] as const);

    identifyAndTrack(Analytics.Event.a_login_succeeded, user, userData);

    return { user, userData };
  } catch (error) {
    Analytics.track({ event: Analytics.Event.a_login_failed, error, from: 'website' });
    throw error;
  }
}

async function signUpUser(details: RegistrationNewUserDetails): Promise<AuthResponse> {
  const { userData = {}, ...userDetails } = details;
  const apiClient = new ApiClient();
  try {
    const { user } = await apiClient.register({
      user: userDetails,
      userData: stripMedicareAccessFromUserData(userData),
    });

    identifyAndTrack(Analytics.Event.a_signup_succeeded, user, userData);

    const userId = user.userId;

    const authUserId = await emailSignIn(details.email, details.password);

    if (userId !== authUserId) {
      throw Error(
        `Recently created userId ${userId} does not match signed in userId ${authUserId}`
      );
    }

    return { user, userData };
  } catch (error) {
    Analytics.track({
      event: Analytics.Event.a_signup_failed,
      error,
      from: 'website',
    });
    throw {
      code: ((error as { details: { code: string } })?.details?.code ?? 'unknown') as AuthErrorCode,
      message: (error as { message: string })?.message ?? 'unknown',
    };
  }
}

async function signUpAndRegisterForPayment(
  data: RegistrationDetails
): Promise<SignupAndRegisterForPaymentResponse> {
  const { userData = {}, lastName, email, password, name, isTemporaryRegister } = data;
  const { user, isFirstTimeUser, userHasPaid } = await new ApiClient().registerForPayment(
    stripUndefined({
      email,
      password,
      name,
      lastName,
      userData: stripMedicareAccessFromUserData(userData),
    })
  );
  const paid = userHasPaid?.paid ?? false;
  const userId = user.userId;
  // If a user is not a first time temp user, it means they have triggered the temp user flow using an existing
  // account. If that is the case, login should not be triggered as the temp register flow uses a generated password
  // and would try to login using a wrong password, causing a login error.
  const isFirstTimeTemporaryRegister = isFirstTimeUser && isTemporaryRegister;
  const requiresLogin = !isTemporaryRegister || isFirstTimeTemporaryRegister || isFirstTimeUser;

  identifyAndTrack(Analytics.Event.a_signup_succeeded, user, userData);

  if (requiresLogin) {
    await emailSignInAndAccountValidation({ email, password, userId });
  }

  return { user, paid, setUserPassword: requiresLogin };
}

async function signOutUser(): Promise<void> {
  const firebaseAuth = firebase.auth();

  try {
    await firebaseAuth.signOut();
    if (firebaseAuth.currentUser !== null) {
      await firebaseAuth.currentUser.delete();
    }
    Analytics.track({ event: Analytics.Event.a_logout_succeeded });
  } catch (error) {
    Analytics.track({
      event: Analytics.Event.a_logout_failed,
      error,
      from: 'website',
    });
    throw error;
  }
}

async function updateCurrentUserPassword(password: string): Promise<void> {
  const firebaseAuth = firebase.auth();
  if (firebaseAuth.currentUser === null) {
    // Our error handling expects a firebase error code
    const error: firebase.auth.Error = {
      code: 'auth/requires-recent-login',
      message: 'updateCurrentUserPassword(...) was called before logging in a user.',
      name: 'AuthError',
    };
    throw error;
  }
  return firebaseAuth.currentUser.updatePassword(password);
}

async function updateCurrentUserName(name: string): Promise<void> {
  const firebaseAuth = firebase.auth();
  if (firebaseAuth.currentUser === null) {
    const error: firebase.auth.Error = {
      code: 'auth/requires-recent-login',
      message: 'updateCurrentUserName(...) was called before logging in a user.',
      name: 'AuthError',
    };
    throw error;
  }
  return getUserDocRef(firebaseAuth.currentUser.uid).set({ name }, { merge: true });
}

async function sendPasswordResetEmail(email: string): Promise<void> {
  const firebaseAuth = firebase.auth();
  return firebaseAuth.sendPasswordResetEmail(email);
}

export const Auth = {
  isAuthError,
  isUserSignedIn,
  signInUser,
  signUpUser,
  signUpAndRegisterForPayment,
  signOutUser,
  updateCurrentUserPassword,
  updateCurrentUserName,
  sendPasswordResetEmail,
};
